import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  Download,
  XCircle,
  RefreshCw,
  Search,
  Eye,
  Ban,
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { TenantComplianceDetail } from './TenantComplianceDetail';
import { SuspendTenantModal } from './SuspendTenantModal';

interface TenantCompliance {
  tenant_id: string;
  tenant_name: string;
  compliance_status: string;
  opt_out_rate: number;
  spam_complaint_rate: number;
  delivery_rate: number;
  total_messages_sent: number;
  opt_outs: number;
  spam_complaints: number;
  messages_delivered: number;
  consent_rate: number;
  warnings: string[];
}

interface ComplianceOverview {
  total_tenants: number;
  status_counts: {
    good: number;
    warning: number;
    critical: number;
    unknown: number;
  };
  aggregate_metrics: {
    total_messages_sent: number;
    total_opt_outs: number;
    total_spam_complaints: number;
    messages_delivered: number;
    platform_opt_out_rate: number;
    platform_spam_rate: number;
    platform_delivery_rate: number;
  };
}

export const ComplianceManagement: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('compliance_status');
  const [sortOrder, setSortOrder] = useState<string>('asc');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [suspendTenantId, setSuspendTenantId] = useState<string | null>(null);

  const { data: overview, isLoading: overviewLoading, refetch: refetchOverview } = useQuery({
    queryKey: ['server-compliance-overview'],
    queryFn: async () => {
      const response = await api.serverAdmin.getComplianceOverview();
      return response.data as ComplianceOverview;
    },
    refetchInterval: 60000,
  });

  const { data: tenants, isLoading: tenantsLoading, refetch: refetchTenants } = useQuery({
    queryKey: ['server-compliance-tenants', statusFilter, sortBy, sortOrder],
    queryFn: async () => {
      const params: any = {
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await api.serverAdmin.getComplianceTenants(params);
      return response.data as TenantCompliance[];
    },
    refetchInterval: 60000,
  });

  const handleExportReport = async () => {
    try {
      toast.info('Generating platform compliance report...');
      const response = await api.serverAdmin.getPlatformComplianceReport();
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `platform-compliance-report-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Platform compliance report downloaded');
    } catch (err: any) {
      console.error('Failed to export report:', err);
      toast.error('Failed to export platform compliance report');
    }
  };

  const handleRefresh = () => {
    refetchOverview();
    refetchTenants();
    toast.success('Compliance data refreshed');
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'good':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'good':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'critical':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return null;
    }
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const filteredTenants = tenants?.filter((tenant) =>
    tenant.tenant_name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  if (overviewLoading || tenantsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600" />
            Compliance Management
          </h2>
          <p className="text-muted-foreground mt-1">
            Monitor and manage SMS compliance across all tenants
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleRefresh} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleExportReport} variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={async () => {
              try {
                if (!selectedTenant) { toast.error('Select a tenant first'); return; }
                const confirmSeed = window.confirm('Seed compliance test data for the selected tenant?');
                if (!confirmSeed) return;
                await api.serverAdmin.seedComplianceTestData({ tenant_id: selectedTenant, messages: 300, opt_out_rate: 0.07, spam_rate: 0.03, delivery_failure_rate: 0.1, days: 30 });
                toast.success('Seeded compliance test data');
                refetchOverview();
                refetchTenants();
              } catch (e) {
                console.error(e);
                toast.error('Failed to seed test data');
              }
            }}
          >
            Seed Test Data
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={async () => {
              try {
                if (!selectedTenant) { toast.error('Select a tenant first'); return; }
                const confirmClear = window.confirm('Clear recent compliance test data for the selected tenant?');
                if (!confirmClear) return;
                await api.serverAdmin.clearComplianceTestData(selectedTenant, 35);
                toast.success('Cleared test data');
                refetchOverview();
                refetchTenants();
              } catch (e) {
                console.error(e);
                toast.error('Failed to clear test data');
              }
            }}
          >
            Clear Test Data
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Tenants</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview?.total_tenants || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Good Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {overview?.status_counts.good || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Warnings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {overview?.status_counts.warning || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Critical Violations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {overview?.status_counts.critical || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search tenants..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="good">Good</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tenants Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tenant Compliance Status</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredTenants.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No tenants found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <button
                        onClick={() => handleSort('tenant_name')}
                        className="flex items-center gap-1 hover:text-blue-600"
                      >
                        Tenant Name
                        {sortBy === 'tenant_name' && (
                          <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        onClick={() => handleSort('compliance_status')}
                        className="flex items-center gap-1 hover:text-blue-600"
                      >
                        Status
                        {sortBy === 'compliance_status' && (
                          <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        onClick={() => handleSort('opt_out_rate')}
                        className="flex items-center gap-1 hover:text-blue-600"
                      >
                        Opt-out Rate
                        {sortBy === 'opt_out_rate' && (
                          <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        onClick={() => handleSort('spam_complaint_rate')}
                        className="flex items-center gap-1 hover:text-blue-600"
                      >
                        Spam Rate
                        {sortBy === 'spam_complaint_rate' && (
                          <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        onClick={() => handleSort('delivery_rate')}
                        className="flex items-center gap-1 hover:text-blue-600"
                      >
                        Delivery Rate
                        {sortBy === 'delivery_rate' && (
                          <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </TableHead>
                    <TableHead>Total Messages</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTenants.map((tenant) => (
                    <TableRow key={tenant.tenant_id}>
                      <TableCell className="font-medium">
                        {tenant.tenant_name}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(tenant.compliance_status)}>
                          <span className="flex items-center gap-1">
                            {getStatusIcon(tenant.compliance_status)}
                            {tenant.compliance_status.toUpperCase()}
                          </span>
                        </Badge>
                      </TableCell>
                      <TableCell>{formatPercentage(tenant.opt_out_rate)}</TableCell>
                      <TableCell>{formatPercentage(tenant.spam_complaint_rate)}</TableCell>
                      <TableCell>{formatPercentage(tenant.delivery_rate)}</TableCell>
                      <TableCell>{formatNumber(tenant.total_messages_sent)}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedTenant(tenant.tenant_id)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          {tenant.compliance_status === 'critical' && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => setSuspendTenantId(tenant.tenant_id)}
                            >
                              <Ban className="h-4 w-4 mr-1" />
                              Suspend
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tenant Detail Modal */}
      {selectedTenant && (
        <TenantComplianceDetail
          tenantId={selectedTenant}
          onClose={() => setSelectedTenant(null)}
          onSuspend={() => {
            setSelectedTenant(null);
            setSuspendTenantId(selectedTenant);
          }}
        />
      )}

      {/* Suspend Tenant Modal */}
      {suspendTenantId && (
        <SuspendTenantModal
          tenantId={suspendTenantId}
          onClose={() => {
            setSuspendTenantId(null);
            refetchTenants();
            refetchOverview();
          }}
        />
      )}
    </div>
  );
};

